import { Button, Collapse, Dialog, DialogActions, DialogContent, DialogTitle, TextField, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import DragDropUpload from '../../../../DragDropUpload';
import { Stack } from '@mui/system';
import { getTextFileTypes } from '../../../../../utils/string';
import { convertWordsToRows, KeyphraseRow } from '../../../../../utils/keyphrases';

interface WordListDialogProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	updateRows: (newRows: KeyphraseRow[]) => void;
}

const WordListDialog: React.FC<WordListDialogProps> = ({ open, setOpen, updateRows }) => {
	const [fileError, setFileError] = useState<string>('');
	const [processing, setProcessing] = useState<boolean>(false);
	const [loadedWords, setLoadedWords] = useState<string[]>([]);
	const [uploadedFile, setUploadedFile] = useState<File | undefined>();

	const resetDialog = () => {
		setFileError('');
		setProcessing(false);
		setLoadedWords([]);
		setUploadedFile(undefined);
	};

	const onClose = () => {
		resetDialog();
		setOpen(false);
	};

	useEffect(() => {
		if (uploadedFile) {
			uploadedFile.text().then((txt) => {
				let newWords: string[] = txt.split('\n');
				newWords = newWords.filter((word) => word);
				setLoadedWords(newWords);
				setProcessing(false);
			});
		}
	}, [uploadedFile]);

	return (
		<Dialog open={open} fullWidth data-testid="add-existing-word-list-dialog">
			<DialogTitle>Upload a Word / Phrase List</DialogTitle>
			<DialogContent>
				<Stack spacing={2}>
					<Typography variant="body1">
						Upload a list of words or phrases, with one item per line. These will be loaded into the Keyphrases List Creation Table,
						allowing you to further tailor the list before completing it
					</Typography>

					<DragDropUpload
						onFileSelect={(f) => {
							setProcessing(true);
							setUploadedFile(f);
						}}
						allowedFormats={getTextFileTypes()}
						helperText={'Click to Upload a File'}
						subText={[
							'This can be in most text-based file formats, but each word or phrase must be on a new line',
							'Maximum file size is 10mb',
						]}
						fileError={fileError}
						setFileError={setFileError}
						disabled={processing}
						maxSizeMB={10}
						disabledReason={'Cannot change upload while the current file is being processed'}
					/>

					{uploadedFile && loadedWords.length > 0 && (
						<>
							<Collapse in={loadedWords.length > 0}>
								<Typography>
									Identified ({loadedWords.length}) words or phrases in &quot;{uploadedFile.name}&quot;, please confirm before submitting:
								</Typography>
							</Collapse>

							<Collapse in={loadedWords.length > 0}>
								<TextField
									multiline
									value={loadedWords.join('\n')}
									disabled
									maxRows={3.5}
									fullWidth
									slotProps={{ htmlInput: { 'aria-label': 'Word or Phrase List Summary' } }}
									data-testid="word-list-dialog-keyword-summary"
									tabIndex={0}
								></TextField>
							</Collapse>
						</>
					)}
				</Stack>
			</DialogContent>

			<DialogActions>
				<Button data-testid="word-list-dialog-cancel-button" onClick={onClose}>
					Cancel
				</Button>
				<Button data-testid="word-list-dialog-reset-button" disabled={!uploadedFile} onClick={resetDialog}>
					Reset
				</Button>
				<Button
					data-testid="word-list-dialog-submit-button"
					disabled={loadedWords.length === 0}
					onClick={() => {
						updateRows(convertWordsToRows(loadedWords));
						onClose();
					}}
				>
					Submit
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default WordListDialog;
